import {
  CheckCircle,
  CloudUploadOutlined,
  Delete,
  EditSharp,
  ErrorOutline,
  InsertDriveFileSharp,
  MoreVertOutlined,
  SearchSharp,
  SystemUpdateAlt,
  WorkOutlineSharp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import type { ChangeEvent, DragEvent, MouseEvent } from "react";
import { useCallback, useReducer, useRef } from "react";
import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx";

import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import CustomDialog from "../../components/CustomDialog";
import TableLoadingSkeleton, {
  type TableSkeletonColumn,
} from "../../components/crm/TableLoadingSkeleton";
import { useSnackbar } from "../../contexts/SnackbarContext";
import useCrmProducts from "../../hooks/useCrmProducts";
import {
  deleteAdminProduct,
  updateAdminProduct,
  uploadFinishedProducts,
  uploadRxProducts,
} from "../../services/order.service";
import {
  Product,
  ProductFilters,
  ProductType,
  ProductUpdateRequest,
} from "../../types/order";

type ImportProductType = "RX" | "FINISHED";

type UploadState = "idle" | "uploading" | "success" | "failed";
type UploadStatus = { state: UploadState; message?: string };

const MAX_IMPORT_FILE_BYTES = 4 * 1024 * 1024;
const VALID_IMPORT_EXT_RE = /\.(xlsx|xls|csv)$/i;
const fileKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

const REQUIRED_IMPORT_COLUMNS = [
  "Code",
  "Custom Description",
  "Search String",
  "Index Code",
  "Comm Diameter",
  "Physical Diameter",
  "Sphere Min Val",
  "Sphere Max Val",
  "Addition Min Val",
  "Addition Max Val",
  "Cylinder Min Val",
  "Cylinder Max Val",
];

type ProductListFilters = ProductFilters;

type ProductFormState = {
  code: string;
  product_type: ProductType;
  description: string;
  custom_description: string;
  search_string: string;
  comm_diameter: string;
  physical_diameter: string;
  index_code: string;
  sphere_min: string;
  sphere_max: string;
  cylinder_min: string;
  cylinder_max: string;
  active: boolean;
};

const initialFormState: ProductFormState = {
  code: "",
  product_type: "RX",
  description: "",
  custom_description: "",
  search_string: "",
  comm_diameter: "",
  physical_diameter: "",
  index_code: "",
  sphere_min: "",
  sphere_max: "",
  cylinder_min: "",
  cylinder_max: "",
  active: true,
};

const PRODUCT_MASTER_SKELETON_COLUMNS: TableSkeletonColumn[] = [
  { key: "checkbox", isCheckbox: true },
  { key: "actions", width: 80 },
  { key: "no", width: 80 },
  { key: "code", width: 124 },
  { key: "index-key", width: 160 },
  { key: "custom-description", width: 346 },
  { key: "search-string", width: 346 },
  { key: "comm-diameter", width: 246 },
  { key: "physical-diameter", width: 173 },
  { key: "sphere-min", width: 157 },
  { key: "sphere-max", width: 157 },
  { key: "cylinder-min", width: 165 },
  { key: "cylinder-max", width: 165 },
  { key: "index-code", width: 165 },
  { key: "fclo", width: 130 },
  { key: "design-code", width: 150 },
  { key: "chromatic-code", width: 165 },
  { key: "material-code", width: 165 },
  { key: "additional-min", width: 157 },
  { key: "additional-max", width: 157 },
  { key: "product-type", width: 140 },
  { key: "active", width: 100 },
];

export default function CrmProductMaster() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  type State = {
    filters: ProductListFilters;
    dialogOpen: boolean;
    deleteDialogOpen: boolean;
    selectedProduct: Product | null;
    formState: ProductFormState;
    isSubmitting: boolean;
    selectedIds: string[];
    actionAnchorEl: HTMLElement | null;
    actionRow: Product | null;
    importDialogOpen: boolean;
    isImportDragActive: boolean;
    importFiles: File[];
    importProductType: ImportProductType;
    importStatuses: UploadStatus[];
    importUploadingIndex: number | null;
    bulkDeleteDialogOpen: boolean;
  };

  type Action =
    | { type: "SET_FILTERS"; payload: Partial<ProductListFilters> }
    | { type: "SET_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_BULK_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_SELECTED_PRODUCT"; payload: Product | null }
    | { type: "SET_FORM_STATE"; payload: ProductFormState }
    | { type: "SET_IS_SUBMITTING"; payload: boolean }
    | { type: "SET_SELECTED_IDS"; payload: string[] }
    | { type: "SET_ACTION_ANCHOR_EL"; payload: HTMLElement | null }
    | { type: "SET_ACTION_ROW"; payload: Product | null }
    | { type: "SET_IMPORT_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_IS_IMPORT_DRAG_ACTIVE"; payload: boolean }
    | { type: "SET_IMPORT_FILES"; payload: File[] }
    | { type: "SET_IMPORT_PRODUCT_TYPE"; payload: ImportProductType }
    | { type: "SET_IMPORT_STATUSES"; payload: UploadStatus[] }
    | {
        type: "UPDATE_IMPORT_STATUS";
        payload: { index: number; status: UploadStatus };
      }
    | { type: "SET_IMPORT_UPLOADING_INDEX"; payload: number | null }
    | {
        type: "OPEN_EDIT_DIALOG";
        payload: { product: Product; form: ProductFormState };
      }
    | { type: "OPEN_CREATE_DIALOG" }
    | { type: "CLOSE_DIALOGS" };

  const initialState: State = {
    filters: {
      search: "",
      page: 1,
      page_size: 10,
      sort: ["created_at|-1"],
    },
    dialogOpen: false,
    deleteDialogOpen: false,
    selectedProduct: null,
    formState: initialFormState,
    isSubmitting: false,
    selectedIds: [],
    actionAnchorEl: null,
    actionRow: null,
    importDialogOpen: false,
    isImportDragActive: false,
    importFiles: [],
    importProductType: "RX",
    importStatuses: [],
    importUploadingIndex: null,
    bulkDeleteDialogOpen: false,
  };

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case "SET_FILTERS":
        return {
          ...state,
          filters: { ...state.filters, ...action.payload },
          selectedIds: [],
        };
      case "SET_DIALOG_OPEN":
        return { ...state, dialogOpen: action.payload };
      case "SET_DELETE_DIALOG_OPEN":
        return { ...state, deleteDialogOpen: action.payload };
      case "SET_SELECTED_PRODUCT":
        return { ...state, selectedProduct: action.payload };
      case "SET_FORM_STATE":
        return { ...state, formState: action.payload };
      case "SET_IS_SUBMITTING":
        return { ...state, isSubmitting: action.payload };
      case "SET_SELECTED_IDS":
        return { ...state, selectedIds: action.payload };
      case "SET_ACTION_ANCHOR_EL":
        return { ...state, actionAnchorEl: action.payload };
      case "SET_ACTION_ROW":
        return { ...state, actionRow: action.payload };
      case "SET_BULK_DELETE_DIALOG_OPEN":
        return { ...state, bulkDeleteDialogOpen: action.payload };
      case "SET_IMPORT_DIALOG_OPEN":
        return { ...state, importDialogOpen: action.payload };
      case "SET_IS_IMPORT_DRAG_ACTIVE":
        return { ...state, isImportDragActive: action.payload };
      case "SET_IMPORT_FILES":
        return { ...state, importFiles: action.payload };
      case "SET_IMPORT_PRODUCT_TYPE":
        return { ...state, importProductType: action.payload };
      case "SET_IMPORT_STATUSES":
        return { ...state, importStatuses: action.payload };
      case "UPDATE_IMPORT_STATUS": {
        const next = state.importStatuses.slice();
        next[action.payload.index] = action.payload.status;
        return { ...state, importStatuses: next };
      }
      case "SET_IMPORT_UPLOADING_INDEX":
        return { ...state, importUploadingIndex: action.payload };
      case "OPEN_EDIT_DIALOG":
        return {
          ...state,
          selectedProduct: action.payload.product,
          formState: action.payload.form,
          dialogOpen: true,
          actionAnchorEl: null,
        };
      case "OPEN_CREATE_DIALOG":
        return {
          ...state,
          selectedProduct: null,
          formState: initialFormState,
          dialogOpen: true,
        };
      case "CLOSE_DIALOGS":
        return {
          ...state,
          dialogOpen: false,
          deleteDialogOpen: false,
          importDialogOpen: false,
        };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    filters,
    dialogOpen,
    deleteDialogOpen,
    selectedProduct,
    formState,
    isSubmitting,
    selectedIds,
    actionAnchorEl,
    actionRow,
    importDialogOpen,
    isImportDragActive,
    importFiles,
    importProductType,
    importStatuses,
    importUploadingIndex,
    bulkDeleteDialogOpen,
  } = state;

  const {
    data,
    dataLoading: isLoading,
    mutate,
  } = useCrmProducts(true, filters);
  const products = data?.data || [];
  const totalItems = data?.total_items ?? data?.total ?? 0;
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { search: value, page: 1 } });
  }, []);

  const handlePageChange = useCallback((_: unknown, page: number) => {
    dispatch({ type: "SET_FILTERS", payload: { page: page + 1 } });
  }, []);

  const handlePageSizeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "SET_FILTERS",
        payload: {
          page_size: parseInt(event.target.value, 10),
          page: 1,
        },
      });
    },
    [],
  );

  const handleProductTypeFilter = useCallback((value: string) => {
    dispatch({
      type: "SET_FILTERS",
      payload: {
        page: 1,
        product_type: value === "all" ? undefined : (value as ProductType),
      },
    });
  }, []);

  const handleOpenDelete = (product: Product) => {
    dispatch({ type: "SET_SELECTED_PRODUCT", payload: product });
    dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleSubmit = async () => {
    if (!formState.code.trim() || !selectedProduct?.id) return;

    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      const payload: ProductUpdateRequest = {
        product_type: formState.product_type,
        description: formState.description,
        custom_description: formState.custom_description,
        search_string: formState.search_string,
        comm_diameter: formState.comm_diameter,
        physical_diameter: formState.physical_diameter,
        index_code: formState.index_code,
        sphere_min: formState.sphere_min,
        sphere_max: formState.sphere_max,
        cylinder_min: formState.cylinder_min,
        cylinder_max: formState.cylinder_max,
        active: formState.active,
      };
      await updateAdminProduct(selectedProduct.id, payload);
      dispatch({ type: "SET_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_PRODUCT", payload: null });
      dispatch({ type: "SET_FORM_STATE", payload: initialFormState });
      mutate();
      showSnackbar("Product updated successfully", "success");
    } catch (error) {
      console.error("Error saving product:", error);
      showSnackbar("Failed to update product", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct?.id) return;

    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await deleteAdminProduct(selectedProduct.id);
      dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_PRODUCT", payload: null });
      mutate();
      showSnackbar("Product deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting product:", error);
      showSnackbar("Failed to delete product", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await Promise.all(selectedIds.map((id) => deleteAdminProduct(id)));
      showSnackbar(
        `Successfully deleted ${selectedIds.length} product(s).`,
        "success",
      );
      dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      mutate();
    } catch (error) {
      console.error("Error deleting products:", error);
      showSnackbar("Failed to delete some products.", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const allSelected =
    products.length > 0 &&
    products.every((row: Product) => row.id && selectedIds.includes(row.id));
  const someSelected =
    selectedIds.length > 0 && !allSelected && products.length > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      return;
    }
    dispatch({
      type: "SET_SELECTED_IDS",
      payload: products.map((row: Product) => row.id || "").filter(Boolean),
    });
  };

  const handleSelectRow = (id: string) => {
    dispatch({
      type: "SET_SELECTED_IDS",
      payload: selectedIds.includes(id)
        ? selectedIds.filter((item) => item !== id)
        : [...selectedIds, id],
    });
  };

  const handleOpenActionMenu = (
    row: Product,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    dispatch({ type: "SET_ACTION_ROW", payload: row });
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: event.currentTarget });
  };

  const handleCloseActionMenu = () => {
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: null });
    dispatch({ type: "SET_ACTION_ROW", payload: null });
  };

  const handleActionEdit = () => {
    if (actionRow?.id) navigate(`/crm/product-master/${actionRow.id}/edit`);
    handleCloseActionMenu();
  };

  const handleActionDelete = () => {
    if (actionRow) handleOpenDelete(actionRow);
    handleCloseActionMenu();
  };

  const handleOpenImportDialog = () => {
    dispatch({ type: "SET_IMPORT_DIALOG_OPEN", payload: true });
  };

  const handleCloseImportDialog = () => {
    if (isSubmitting) return;
    dispatch({ type: "SET_IMPORT_DIALOG_OPEN", payload: false });
    dispatch({ type: "SET_IS_IMPORT_DRAG_ACTIVE", payload: false });
    dispatch({ type: "SET_IMPORT_FILES", payload: [] });
    dispatch({ type: "SET_IMPORT_PRODUCT_TYPE", payload: "RX" });
    dispatch({ type: "SET_IMPORT_STATUSES", payload: [] });
    dispatch({ type: "SET_IMPORT_UPLOADING_INDEX", payload: null });
  };

  const addFilesToImport = (incoming: File[]) => {
    if (incoming.length === 0) return;
    const rejected: string[] = [];
    const existingKeys = new Set(importFiles.map(fileKey));
    const accepted: File[] = [];
    for (const file of incoming) {
      if (!VALID_IMPORT_EXT_RE.test(file.name)) {
        rejected.push(`${file.name} (unsupported type)`);
        continue;
      }
      if (file.size > MAX_IMPORT_FILE_BYTES) {
        rejected.push(`${file.name} (exceeds 4 MB)`);
        continue;
      }
      const key = fileKey(file);
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      accepted.push(file);
    }
    if (rejected.length > 0) {
      showSnackbar(`Some files were skipped: ${rejected.join(", ")}`, "error");
    }
    if (accepted.length === 0) return;
    const merged = [...importFiles, ...accepted].slice(0, 100);
    dispatch({ type: "SET_IMPORT_FILES", payload: merged });
    dispatch({
      type: "SET_IMPORT_STATUSES",
      payload: merged.map(() => ({ state: "idle" as UploadState })),
    });
  };

  const handleImportFileInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    addFilesToImport(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const handleImportDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    dispatch({ type: "SET_IS_IMPORT_DRAG_ACTIVE", payload: true });
  };

  const handleImportDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dispatch({ type: "SET_IS_IMPORT_DRAG_ACTIVE", payload: false });
  };

  const handleImportDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dispatch({ type: "SET_IS_IMPORT_DRAG_ACTIVE", payload: false });
    if (isSubmitting) return;
    addFilesToImport(Array.from(event.dataTransfer.files || []));
  };

  const handleDeleteImportFile = (index: number) => {
    if (isSubmitting) return;
    dispatch({
      type: "SET_IMPORT_FILES",
      payload: importFiles.filter((_, fileIndex) => fileIndex !== index),
    });
    dispatch({
      type: "SET_IMPORT_STATUSES",
      payload: importStatuses.filter((_, i) => i !== index),
    });
  };

  const SAMPLE_ROWS = [
    REQUIRED_IMPORT_COLUMNS,
    ["PRD001", "Progressive HD", "progressive_hd", "1.60", "60/70", "70", "-10", "6", "0.75", "6.5", "-6", "-0.25"],
    ["PRD002", "Sun Polarized", "sun_polarized", "1.50", "60/65", "65", "0", "0", "0", "0", "0", "0"],
  ];

  const handleDownloadSampleXlsx = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(SAMPLE_ROWS);
    worksheet["!cols"] = SAMPLE_ROWS[0].map(() => ({ wch: 20 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
    XLSX.writeFile(workbook, "sample_import_products.xlsx");
  };

  const handleDownloadSampleCsv = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(SAMPLE_ROWS);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_import_products.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const uploadFilesSequentially = async (indices: number[]) => {
    const uploader =
      importProductType === "FINISHED"
        ? uploadFinishedProducts
        : uploadRxProducts;

    let created = 0;
    let updated = 0;
    let total = 0;
    let successCount = 0;
    let failCount = 0;

    for (const i of indices) {
      dispatch({ type: "SET_IMPORT_UPLOADING_INDEX", payload: i });
      dispatch({
        type: "UPDATE_IMPORT_STATUS",
        payload: { index: i, status: { state: "uploading" } },
      });
      try {
        const formData = new FormData();
        formData.append("file", importFiles[i]);
        const result = await uploader(formData);
        created += result?.created ?? 0;
        updated += result?.updated ?? 0;
        total += result?.total ?? 0;
        successCount += 1;
        dispatch({
          type: "UPDATE_IMPORT_STATUS",
          payload: { index: i, status: { state: "success" } },
        });
      } catch (err: unknown) {
        failCount += 1;
        const msg =
          err instanceof Error ? err.message : "Upload failed.";
        dispatch({
          type: "UPDATE_IMPORT_STATUS",
          payload: { index: i, status: { state: "failed", message: msg } },
        });
      }
    }

    dispatch({ type: "SET_IMPORT_UPLOADING_INDEX", payload: null });
    return { created, updated, total, successCount, failCount };
  };

  const handleSubmitImportData = async () => {
    if (importFiles.length === 0 || isSubmitting) return;

    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      const targets =
        importStatuses.some((s) => s?.state === "failed")
          ? importStatuses
              .map((s, i) => (s?.state === "failed" ? i : -1))
              .filter((i) => i >= 0)
          : importFiles.map((_, i) => i);

      const { created, updated, total, successCount, failCount } =
        await uploadFilesSequentially(targets);

      if (failCount === 0) {
        showSnackbar(
          `Import completed (${importProductType}): ${created} created, ${updated} updated, ${total} total.`,
          "success",
        );
        handleCloseImportDialog();
        mutate();
      } else {
        showSnackbar(
          `Imported ${successCount} of ${targets.length} file(s). ${failCount} failed — review and retry.`,
          "error",
        );
        if (successCount > 0) mutate();
      }
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) {
      const mb = Math.round((bytes / (1024 * 1024)) * 10) / 10;
      return `${Number.isInteger(mb) ? mb.toFixed(0) : mb.toFixed(1)} MB`;
    }
    return `${Math.round(bytes / 1024)} KB`;
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
          <Box
            sx={{
              p: 2,
              borderBottom: (theme) =>
                `1px solid ${theme.vars.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                placeholder="Search"
                value={filters.search || ""}
                onChange={(event) => handleSearchChange(event.target.value)}
                sx={{ width: 220 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchSharp fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1.5}>
              {selectedIds.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() =>
                    dispatch({
                      type: "SET_BULK_DELETE_DIALOG_OPEN",
                      payload: true,
                    })
                  }
                  sx={{
                    borderRadius: "999px",
                    px: 2.25,
                    textTransform: "none",
                    minHeight: 40,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  Delete Selected ({selectedIds.length})
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<SystemUpdateAlt />}
                onClick={handleOpenImportDialog}
                sx={{
                  borderRadius: "999px",
                  bgcolor: "#424242",
                  color: "#fff",
                  px: 2.25,
                  textTransform: "none",
                  minHeight: 40,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#2f2f2f", boxShadow: "none" },
                }}
              >
                Import Data
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/crm/product-master/create")}
                sx={{
                  borderRadius: "999px",
                  bgcolor: "#111",
                  color: "#fff",
                  px: 2.5,
                  textTransform: "none",
                  minHeight: 40,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#000", boxShadow: "none" },
                }}
              >
                + Create Product Master
              </Button>
            </Stack>
          </Box>

          {isLoading ? (
            <TableLoadingSkeleton
              columns={PRODUCT_MASTER_SKELETON_COLUMNS}
              rowCount={8}
              minWidth={3500}
            />
          ) : (
            <TableContainer
              sx={{
                overflowX: "auto",
                position: "relative",
                borderTop: (theme) => `1px solid ${theme.vars.palette.divider}`,
              }}
            >
              <Table
                sx={{
                  minWidth: 3500,
                  "& th, & td": {
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        height: 52,
                        borderBottom: `1px solid ${theme.vars.palette.divider}`,
                        color: "#25272C",
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleSelectAll}
                        sx={{
                          "&.Mui-checked": { color: "#1976D2" },
                          "&.MuiCheckbox-indeterminate": { color: "#1976D2" },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 80 }}>ACTIONS</TableCell>
                    <TableCell sx={{ width: 80 }}>NO.</TableCell>
                    <TableCell sx={{ width: 124 }}>CODE</TableCell>
                    <TableCell sx={{ width: 160 }}>INDEX KEY</TableCell>
                    <TableCell sx={{ width: 346 }}>CUSTOM DESCRIPTION</TableCell>
                    <TableCell sx={{ width: 346 }}>SEARCH STRING</TableCell>
                    <TableCell sx={{ width: 246 }}>COMM DIAMETER</TableCell>
                    <TableCell sx={{ width: 173 }}>PHYSICAL DIAMETER</TableCell>
                    <TableCell sx={{ width: 157 }}>SPHERE MIN VALUE</TableCell>
                    <TableCell sx={{ width: 157 }}>SPHERE MAX VALUE</TableCell>
                    <TableCell sx={{ width: 165 }}>CYLINDER MIN VALUE</TableCell>
                    <TableCell sx={{ width: 165 }}>CYLINDER MAX VALUE</TableCell>
                    <TableCell sx={{ width: 165 }}>INDEX CODE</TableCell>
                    <TableCell sx={{ width: 130 }}>FCLO</TableCell>
                    <TableCell sx={{ width: 150 }}>DESIGN CODE</TableCell>
                    <TableCell sx={{ width: 165 }}>CHROMATIC CODE</TableCell>
                    <TableCell sx={{ width: 165 }}>MATERIAL CODE</TableCell>
                    <TableCell sx={{ width: 157 }}>ADDITIONAL MIN</TableCell>
                    <TableCell sx={{ width: 157 }}>ADDITIONAL MAX</TableCell>
                    <TableCell sx={{ width: 140 }}>PRODUCT TYPE</TableCell>
                    <TableCell sx={{ width: 100 }}>ACTIVE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product: Product, index: number) => (
                    <TableRow
                      key={product.id || product.index_key || `${product.code}-${index}`}
                      selected={!!product.id && selectedIds.includes(product.id)}
                      sx={{
                        "&.Mui-selected, &.Mui-selected:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                        "& .MuiTableCell-root": {
                          height: 79,
                          borderBottom: `1px solid ${theme.vars.palette.divider}`,
                          fontSize: 12,
                          color: "#25272C",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            !!product.id && selectedIds.includes(product.id)
                          }
                          onChange={() =>
                            product.id && handleSelectRow(product.id)
                          }
                          sx={{ "&.Mui-checked": { color: "#1976D2" } }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) =>
                            handleOpenActionMenu(product, event)
                          }
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "4px",
                            bgcolor:
                              actionRow?.id === product.id &&
                              Boolean(actionAnchorEl)
                                ? "#EDEEF1"
                                : "transparent",
                            "&:hover": { bgcolor: "#EDEEF1" },
                          }}
                        >
                          <MoreVertOutlined fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {index +
                          1 +
                          ((filters.page || 1) - 1) *
                            (filters.page_size || 10)}
                      </TableCell>
                      <TableCell>{product.code || "-"}</TableCell>
                      <TableCell>{product.index_key || "-"}</TableCell>
                      <TableCell>
                        {product.custom_description ||
                          product.description ||
                          "-"}
                      </TableCell>
                      <TableCell>{product.search_string || "-"}</TableCell>
                      <TableCell>{product.comm_diameter || "-"}</TableCell>
                      <TableCell>
                        {product.physical_diameter || "-"}
                      </TableCell>
                      <TableCell>{product.sphere_min || "-"}</TableCell>
                      <TableCell>{product.sphere_max || "-"}</TableCell>
                      <TableCell>{product.cylinder_min || "-"}</TableCell>
                      <TableCell>{product.cylinder_max || "-"}</TableCell>
                      <TableCell>{product.index_code || "-"}</TableCell>
                      <TableCell>{product.fclo || "-"}</TableCell>
                      <TableCell>{product.design_code || "-"}</TableCell>
                      <TableCell>{product.chromatic_code || "-"}</TableCell>
                      <TableCell>{product.material_code || "-"}</TableCell>
                      <TableCell>{product.additional_min || "-"}</TableCell>
                      <TableCell>{product.additional_max || "-"}</TableCell>
                      <TableCell>{product.product_type || "-"}</TableCell>
                      <TableCell>
                        {product.active != null
                          ? product.active
                            ? "Yes"
                            : "No"
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {products.length === 0 && (
                <Box
                  sx={{
                    position: "sticky",
                    left: 0,
                    width: "100%",
                    minHeight: 430,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 6,
                    borderTop: (theme) =>
                      `1px solid ${theme.vars.palette.divider}`,
                    backgroundColor: "#fff",
                  }}
                >
                  <Stack alignItems="center" justifyContent="center" spacing={1}>
                    <WorkOutlineSharp sx={{ color: "#9BA4B4", fontSize: 40 }} />
                    <Typography
                      sx={{
                        color: "#8E95A2",
                        fontSize: 16,
                        textAlign: "center",
                      }}
                    >
                      No initial data available
                      <br />
                      Product master data was not found.
                    </Typography>
                  </Stack>
                </Box>
              )}
            </TableContainer>
          )}

          <Box
            sx={{
              borderTop: (theme) => `1px solid ${theme.vars.palette.divider}`,
            }}
          >
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 20, 50]}
              count={totalItems}
              rowsPerPage={filters.page_size || 10}
              page={(filters.page || 1) - 1}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
            />
          </Box>
        </Paper>
      </Box>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleCloseActionMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              borderRadius: "8px",
              boxShadow:
                "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
          list: {
            sx: {
              p: 0,
            },
          },
        }}
      >
        <MenuItem
          onClick={handleActionEdit}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <EditSharp sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Edit"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleActionDelete}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <Delete sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
      </Menu>

      <CustomDialog
        open={importDialogOpen}
        title="Import Data"
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        titleStyle={{
          fontSize: 20,
          fontWeight: 600,
          lineHeight: 1.4,
          color: "#25272C",
        }}
        PaperProps={{
          sx: {
            borderRadius: "4px",
            width: 552,
            maxWidth: "calc(100% - 32px)",
          },
        }}
        content={
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  color: "#25272C",
                }}
              >
                Product type
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                disabled={isSubmitting}
                value={importProductType}
                onChange={(_, value) => {
                  if (value)
                    dispatch({
                      type: "SET_IMPORT_PRODUCT_TYPE",
                      payload: value as ImportProductType,
                    });
                }}
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    px: 2.5,
                    minHeight: 32,
                    fontSize: 14,
                    lineHeight: 1.4,
                    borderColor: "#D8DBDF",
                    color: "#25272C",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    bgcolor: "#111111",
                    color: "#FFFFFF",
                    "&:hover": { bgcolor: "#25272C" },
                  },
                }}
              >
                <ToggleButton value="RX">RX</ToggleButton>
                <ToggleButton value="FINISHED">FINISHED</ToggleButton>
              </ToggleButtonGroup>
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: 1.4,
                  color: "#8E95A2",
                }}
              >
                Required columns: {REQUIRED_IMPORT_COLUMNS.join(", ")}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: 1.4,
                  color: "#8E95A2",
                }}
              >
                You can download a sample file to view the required data format
              </Typography>
              <Stack direction="row" spacing={1}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: "#0054FC",
                  }}
                  onClick={handleDownloadSampleXlsx}
                >
                  Download sample (.xlsx)
                </Link>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: "#0054FC",
                  }}
                  onClick={handleDownloadSampleCsv}
                >
                  Download sample (.csv)
                </Link>
              </Stack>
            </Stack>

            {importFiles.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: "6px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                    lineHeight: 1.4,
                    color: "#25272C",
                  }}
                >
                  Upload Product File
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadOutlined sx={{ fontSize: 20 }} />}
                  onClick={() => importFileInputRef.current?.click()}
                  disabled={isSubmitting}
                  sx={{
                    px: 2,
                    borderRadius: "100px",
                    minHeight: 32,
                    textTransform: "none",
                    fontSize: 16,
                    lineHeight: 1.4,
                    fontWeight: 400,
                    bgcolor: "#111111",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#25272C", boxShadow: "none" },
                  }}
                >
                  Upload
                </Button>
              </Box>
            ) : (
              <Typography
                sx={{
                  py: "6px",
                  fontSize: 16,
                  lineHeight: 1.4,
                  color: "#25272C",
                }}
              >
                Attach Product Master File
              </Typography>
            )}

            <Box
              onDrop={handleImportDrop}
              onDragOver={handleImportDragOver}
              onDragLeave={handleImportDragLeave}
              sx={{
                height: 228,
                borderRadius: 1,
                border: "1px solid #D8DBDF",
                bgcolor: isImportDragActive ? "#F7F8F8" : "#FFFFFF",
                p: 2,
                overflowY: "auto",
                pointerEvents: isSubmitting ? "none" : "auto",
              }}
            >
              {importFiles.length > 0 ? (
                <Stack spacing={1}>
                  {importFiles.map((file, index) => {
                    const status = importStatuses[index]?.state ?? "idle";
                    const errMsg = importStatuses[index]?.message;
                    const nameColor =
                      status === "failed" ? "#B3261E" : "#25272C";
                    return (
                      <Box
                        key={`${fileKey(file)}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: "6px",
                          opacity:
                            isSubmitting &&
                            status !== "uploading" &&
                            status !== "failed"
                              ? 0.6
                              : 1,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ minWidth: 0, flex: 1 }}
                        >
                          <InsertDriveFileSharp
                            sx={{ fontSize: 24, color: "#6B7280" }}
                          />
                          <Stack sx={{ minWidth: 0 }}>
                            <Typography
                              noWrap
                              sx={{
                                fontSize: 16,
                                lineHeight: 1.5,
                                letterSpacing: "0.15px",
                                color: nameColor,
                              }}
                            >
                              {file.name} ({formatFileSize(file.size)})
                            </Typography>
                            {status === "failed" && errMsg && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  lineHeight: 1.4,
                                  color: "#B3261E",
                                }}
                              >
                                {errMsg}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {status === "uploading" && (
                            <CircularProgress size={18} sx={{ color: "#0054FC" }} />
                          )}
                          {status === "success" && (
                            <Tooltip title="Uploaded">
                              <CheckCircle
                                sx={{ fontSize: 22, color: "#2E7D32" }}
                              />
                            </Tooltip>
                          )}
                          {status === "failed" && (
                            <Tooltip title={errMsg || "Upload failed"}>
                              <ErrorOutline
                                sx={{ fontSize: 22, color: "#B3261E" }}
                              />
                            </Tooltip>
                          )}
                          <IconButton
                            onClick={() => handleDeleteImportFile(index)}
                            disabled={isSubmitting}
                            sx={{ color: "#F44336" }}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Stack
                  sx={{
                    height: "100%",
                    border: "1px dashed #6B7280",
                    borderRadius: 1,
                    bgcolor: "#EDEEF1",
                    px: 2,
                    py: 2,
                  }}
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                >
                  <Stack spacing={0.5} alignItems="center">
                    <Typography sx={{ color: "#5F6D78", fontSize: 16 }}>
                      Drag and drop file here or click
                    </Typography>
                    <Typography sx={{ color: "#9CADB6", fontSize: 14 }}>
                      Upload up to 100 file
                    </Typography>
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadOutlined sx={{ fontSize: 24 }} />}
                    onClick={() => importFileInputRef.current?.click()}
                    disabled={isSubmitting}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: "96px",
                      minHeight: 40,
                      textTransform: "none",
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: 1.5,
                      bgcolor: "#111111",
                      boxShadow:
                        "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
                      "&:hover": { bgcolor: "#25272C" },
                    }}
                  >
                    Upload
                  </Button>
                </Stack>
              )}
            </Box>

            {importFiles.length > 0 &&
              (() => {
                const successCount = importStatuses.filter(
                  (s) => s?.state === "success",
                ).length;
                const failedCount = importStatuses.filter(
                  (s) => s?.state === "failed",
                ).length;
                const hasFailures = failedCount > 0;
                const doneCount = successCount + failedCount;
                const progressPct =
                  importFiles.length === 0
                    ? 0
                    : Math.round((doneCount / importFiles.length) * 100);
                const submitLabel = isSubmitting
                  ? `Uploading ${(importUploadingIndex ?? 0) + 1} of ${importFiles.length}...`
                  : hasFailures
                    ? `Retry ${failedCount} failed`
                    : "Import";
                return (
                  <>
                    {(isSubmitting || doneCount > 0) && (
                      <Stack spacing={0.75}>
                        <LinearProgress
                          variant="determinate"
                          value={isSubmitting ? progressPct : 100}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "#EDEEF1",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: hasFailures ? "#B3261E" : "#2E7D32",
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 12,
                            lineHeight: 1.4,
                            color: "#5F6D78",
                          }}
                        >
                          {isSubmitting
                            ? `Uploading ${doneCount + 1} of ${importFiles.length}…`
                            : `Completed: ${successCount} succeeded${
                                failedCount > 0 ? `, ${failedCount} failed` : ""
                              }`}
                        </Typography>
                      </Stack>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: "6px",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 16, lineHeight: 1.4, color: "#25272C" }}
                      >
                        Uploaded: {importFiles.length}/100
                      </Typography>
                      <Typography
                        sx={{ fontSize: 14, lineHeight: 1.4, color: "#8E95A2" }}
                      >
                        .xlsx / .csv · max 4 MB per file
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={handleCloseImportDialog}
                        disabled={isSubmitting}
                        sx={{
                          borderRadius: "96px",
                          px: 3,
                          minHeight: 40,
                          textTransform: "none",
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: 1.5,
                          borderColor: "#111111",
                          color: "#111111",
                          "&:hover": {
                            borderColor: "#111111",
                            bgcolor: "#F7F8F8",
                          },
                        }}
                      >
                        {hasFailures ? "Close" : "Cancel"}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmitImportData}
                        disabled={importFiles.length === 0 || isSubmitting}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: "#8E95A2" }}
                            />
                          ) : undefined
                        }
                        sx={{
                          borderRadius: "96px",
                          px: 3,
                          minHeight: 40,
                          textTransform: "none",
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: 1.5,
                          bgcolor: "#111111",
                          boxShadow:
                            "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
                          "&:hover": { bgcolor: "#25272C" },
                          "&.Mui-disabled": {
                            bgcolor: "#D8DBDF",
                            color: "#8E95A2",
                          },
                        }}
                      >
                        {submitLabel}
                      </Button>
                    </Stack>
                  </>
                );
              })()}

            <input
              ref={importFileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              multiple
              hidden
              onChange={handleImportFileInputChange}
            />
          </Stack>
        }
      />

      <CustomDialog
        open={dialogOpen}
        onClose={() => dispatch({ type: "SET_DIALOG_OPEN", payload: false })}
        title="Edit Product Master"
        maxWidth="md"
        content={
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Code"
                value={formState.code}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: { ...formState, code: event.target.value },
                  })
                }
                disabled={!!selectedProduct}
                required
              />
              <FormControl fullWidth size="small">
                <Select
                  value={formState.product_type}
                  onChange={(event) =>
                    dispatch({
                      type: "SET_FORM_STATE",
                      payload: {
                        ...formState,
                        product_type: event.target.value as ProductType,
                      },
                    })
                  }
                >
                  <MenuItem value="RX">RX</MenuItem>
                  <MenuItem value="FINISHED">FINISHED</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <TextField
              label="Custom Description"
              value={formState.custom_description}
              onChange={(event) =>
                dispatch({
                  type: "SET_FORM_STATE",
                  payload: {
                    ...formState,
                    custom_description: event.target.value,
                  },
                })
              }
            />
            <TextField
              label="Search String"
              value={formState.search_string}
              onChange={(event) =>
                dispatch({
                  type: "SET_FORM_STATE",
                  payload: { ...formState, search_string: event.target.value },
                })
              }
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Comm Diameter"
                value={formState.comm_diameter}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: {
                      ...formState,
                      comm_diameter: event.target.value,
                    },
                  })
                }
              />
              <TextField
                fullWidth
                label="Physical Diameter"
                value={formState.physical_diameter}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: {
                      ...formState,
                      physical_diameter: event.target.value,
                    },
                  })
                }
              />
              <TextField
                fullWidth
                label="Index Code"
                value={formState.index_code}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: { ...formState, index_code: event.target.value },
                  })
                }
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Sphere Min"
                value={formState.sphere_min}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: { ...formState, sphere_min: event.target.value },
                  })
                }
              />
              <TextField
                fullWidth
                label="Sphere Max"
                value={formState.sphere_max}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: { ...formState, sphere_max: event.target.value },
                  })
                }
              />
              <TextField
                fullWidth
                label="Cylinder Min"
                value={formState.cylinder_min}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: { ...formState, cylinder_min: event.target.value },
                  })
                }
              />
              <TextField
                fullWidth
                label="Cylinder Max"
                value={formState.cylinder_max}
                onChange={(event) =>
                  dispatch({
                    type: "SET_FORM_STATE",
                    payload: { ...formState, cylinder_max: event.target.value },
                  })
                }
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button
                onClick={() =>
                  dispatch({ type: "SET_DIALOG_OPEN", payload: false })
                }
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting || !formState.code.trim()}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={deleteDialogOpen}
        onClose={() =>
          dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
        }
        title="Delete Product"
        content={
          <Typography>
            Are you sure you want to delete this product? This action cannot be
            undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
              }
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={isSubmitting || !selectedProduct?.id}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() =>
          dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: false })
        }
        title="Delete Selected Products"
        content={
          <Typography>
            Are you sure you want to delete {selectedIds.length} selected
            product(s)? This action cannot be undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({
                  type: "SET_BULK_DELETE_DIALOG_OPEN",
                  payload: false,
                })
              }
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBulkDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
}
